import React from "react";

import { formatDateOnly } from "../../../../../utils/datetime";
import { availabilityLabel } from "../../utils/listingHelpers";
import ListingFieldGroup from "../components/ListingFieldGroup";
import { choiceRow, fieldHint, fieldInput, fieldLabel } from "../listingEditorStyles";

/**
 * "Veröffentlichung": what makes the listing public, and in what order.
 *
 * Three groups. Visibility and sort order are the only things this section
 * writes, and both go out through the form's single save — there is no second
 * publish path here. The table's row menu still writes `is_published` with its
 * own PATCH, and since L6-SAFETY that PATCH syncs this checkbox for the listing
 * currently open, so the two controls can no longer contradict each other.
 *
 * Availability is read-only on purpose. `availability_status` is PATCH-only
 * from the overview's row menu and has never been part of this form's payload;
 * `available_from` belongs to Basisinformationen. Repeating either as a control
 * here would create a second writer for a field with one owner.
 */

function SummaryRow({ label, children }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-border py-2 last:border-0">
      <dt className="text-[13px] text-muted-foreground">{label}</dt>
      <dd className="m-0 text-[13px] font-medium text-foreground">{children}</dd>
    </div>
  );
}

export default function ListingPublishingSection({
  form,
  onChange,
  availabilityStatus,
}) {
  return (
    <>
      <ListingFieldGroup
        title="Sichtbarkeit"
        description="Steuert, ob das Inserat öffentlich auf der Website erscheint."
      >
        <div>
          <label className={choiceRow}>
            <input
              type="checkbox"
              name="is_published"
              checked={form.is_published}
              onChange={onChange}
            />
            Auf der Website veröffentlichen
          </label>
          <p className={fieldHint}>
            {form.is_published
              ? "Online — das Inserat ist zur Veröffentlichung aktiviert."
              : "Offline — das Inserat ist derzeit nicht öffentlich sichtbar."}
          </p>
        </div>
      </ListingFieldGroup>

      <ListingFieldGroup
        title="Sortierung"
        description="Kleinere Werte werden in der Inseratsliste weiter oben angezeigt."
      >
        <div className="max-w-[220px]">
          <label htmlFor="listing-sort-order" className={fieldLabel}>
            Sortierreihenfolge
          </label>
          {/* `min` mirrors the server contract
              (`sort_order: int = Field(default=0, ge=0)`), so a negative value
              is caught here instead of coming back as a 422. A constraint on a
              control inside a mount-all panel used to leave the save button
              dead — the browser refuses the submit and cannot show the message
              on a hidden control. ListingEditorShell now opens the panel and
              focuses the control, so the constraint is safe to state. */}
          <input
            id="listing-sort-order"
            type="number"
            name="sort_order"
            min={0}
            value={form.sort_order}
            onChange={onChange}
            className={fieldInput}
          />
          <p className={fieldHint}>
            Bei gleichem Wert entscheidet das Erstellungsdatum.
          </p>
        </div>
      </ListingFieldGroup>

      <ListingFieldGroup
        title="Verfügbarkeit"
        description="Wird über das Zeilenmenü der Übersicht bzw. unter Basisinformationen gepflegt und hier nur zusammengefasst."
      >
        <dl className="m-0">
          <SummaryRow label="Status">
            {availabilityLabel(availabilityStatus)}
          </SummaryRow>
          <SummaryRow label="Verfügbar ab">
            {form.available_from
              ? formatDateOnly(form.available_from)
              : "Sofort verfügbar"}
          </SummaryRow>
        </dl>
      </ListingFieldGroup>
    </>
  );
}
